import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import axios from 'axios';
import KioskAnnouncementBanner from '../kiosk/KioskAnnouncementBanner';

const MAX_ITEMS = 3;
const MAX_TEXT_LENGTH = 320;

const DEFAULT_FORM = {
  enabled: true,
  label: 'ANNOUNCEMENTS',
  rotationSeconds: 8,
  style: {
    accentColor: '#1976d2',
    headerBg: '#e3f2fd',
    headerText: '#1565c0',
    borderColor: '#1976d2'
  },
  items: [{ id: '1', text: '', enabled: true }]
};

function normalizeLoaded(data) {
  const base = data && typeof data === 'object' ? data : {};
  const items = Array.isArray(base.items) && base.items.length
    ? base.items.slice(0, MAX_ITEMS).map((item, index) => ({
        id: item.id || `item-${index + 1}`,
        text: item.text || '',
        enabled: item.enabled !== false
      }))
    : [{ id: '1', text: '', enabled: true }];
  return {
    enabled: base.enabled !== false,
    label: base.label || DEFAULT_FORM.label,
    rotationSeconds: base.rotationSeconds ?? DEFAULT_FORM.rotationSeconds,
    style: { ...DEFAULT_FORM.style, ...(base.style || {}) },
    items
  };
}

const KioskAnnouncementsManager = () => {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get('/api/admin/settings/kiosk-announcements');
      const data = res?.data?.data ?? res?.data;
      setForm(normalizeLoaded(data));
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load kiosk announcements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateStyle = (key, value) => {
    setForm((prev) => ({
      ...prev,
      style: { ...prev.style, [key]: value }
    }));
  };

  const updateItem = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, ...patch } : item))
    }));
  };

  const addItem = () => {
    setForm((prev) => {
      if (prev.items.length >= MAX_ITEMS) return prev;
      return {
        ...prev,
        items: [...prev.items, { id: `new-${Date.now()}`, text: '', enabled: true }]
      };
    });
  };

  const removeItem = (index) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        ...form,
        items: form.items
          .map((item) => ({
            ...item,
            text: String(item.text || '').trim().slice(0, MAX_TEXT_LENGTH)
          }))
          .filter((item) => item.text)
      };
      const res = await axios.put('/api/admin/settings/kiosk-announcements', payload);
      const data = res?.data?.data ?? res?.data;
      setForm(normalizeLoaded(data));
      setSuccess('Kiosk announcements saved.');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const previewConfig = {
    enabled: form.enabled,
    label: form.label,
    rotationSeconds: form.rotationSeconds,
    style: form.style,
    items: form.items.filter((item) => item.enabled && String(item.text || '').trim())
  };

  return (
    <Card>
      <CardHeader title="Kiosk Announcements" />
      <Divider />
      <CardContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Banner shown at the top of the Data Collection kiosk. Up to {MAX_ITEMS} messages rotate on a
          timer. Admin only.
        </Typography>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        ) : null}
        {success ? (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        ) : null}

        {loading ? (
          <Typography color="text.secondary">Loading…</Typography>
        ) : (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={form.enabled}
                  onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
                />
              }
              label="Show announcement banner on kiosk"
              sx={{ mb: 2, display: 'block' }}
            />

            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, mb: 2 }}>
              <TextField
                size="small"
                label="Banner label"
                value={form.label}
                onChange={(e) => setForm((prev) => ({ ...prev, label: e.target.value }))}
                inputProps={{ maxLength: 40 }}
              />
              <TextField
                size="small"
                type="number"
                label="Rotation (seconds)"
                value={form.rotationSeconds}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, rotationSeconds: e.target.value }))
                }
                inputProps={{ min: 4, max: 60 }}
              />
              <TextField
                size="small"
                label="Accent / icon color"
                value={form.style.accentColor}
                onChange={(e) => updateStyle('accentColor', e.target.value)}
                placeholder="#1976d2"
              />
              <TextField
                size="small"
                label="Border color"
                value={form.style.borderColor}
                onChange={(e) => updateStyle('borderColor', e.target.value)}
                placeholder="#1976d2"
              />
              <TextField
                size="small"
                label="Label background"
                value={form.style.headerBg}
                onChange={(e) => updateStyle('headerBg', e.target.value)}
                placeholder="#e3f2fd"
              />
              <TextField
                size="small"
                label="Label text color"
                value={form.style.headerText}
                onChange={(e) => updateStyle('headerText', e.target.value)}
                placeholder="#1565c0"
              />
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Messages ({form.items.length}/{MAX_ITEMS})
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
              {form.items.map((item, index) => (
                <Box
                  key={item.id || index}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-start',
                    flexWrap: 'wrap'
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    label={`Announcement ${index + 1}`}
                    value={item.text}
                    onChange={(e) => updateItem(index, { text: e.target.value.slice(0, MAX_TEXT_LENGTH) })}
                    sx={{ flex: '1 1 280px' }}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.5 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={item.enabled}
                          onChange={(e) => updateItem(index, { enabled: e.target.checked })}
                        />
                      }
                      label="On"
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeItem(index)}
                      disabled={form.items.length <= 1}
                      aria-label="Remove announcement"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
              <Button
                size="small"
                startIcon={<Add />}
                onClick={addItem}
                disabled={form.items.length >= MAX_ITEMS}
              >
                Add announcement
              </Button>
              <Button variant="contained" size="small" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Preview
            </Typography>
            <KioskAnnouncementBanner config={previewConfig} />
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default KioskAnnouncementsManager;
